// 'use client'
import React, { createContext, useContext, useEffect, useState } from "react";
import { EmailAuthProvider, GoogleAuthProvider, onAuthStateChanged, reauthenticateWithCredential, reauthenticateWithPopup, signInWithPopup, signInWithRedirect } from "firebase/auth";
import { auth, db } from "../firebase";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut as firebaseSignOut } from "firebase/auth";
import { arrayRemove, arrayUnion, deleteDoc, doc, getDoc, setDoc, updateDoc } from "firebase/firestore";

// Removed the TypeScript interfaces for UserProfile and AuthContextType

const AuthContext = createContext(undefined);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [travelHistory, setTravelHistory] = useState([]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        await fetchUserProfile(user.uid);
      } else {
        setUser(null);
        setProfile(null);
        setTravelHistory([]);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const signUp = async (email, password) => {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Create a new user document in Firestore
    const userProfile = {
      email: user.email,
      displayName: user.displayName || "",
      travelHistory: [],
    };
    await setDoc(doc(db, "users", user.uid), userProfile);
  };

  const signIn = async (email, password) => {
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
      // console.error("Error signing in:", error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    const provider = new GoogleAuthProvider();
    const userCredential = await signInWithPopup(auth, provider);
    const user = userCredential.user;

    // Check if the user document already exists
    const userDocRef = doc(db, "users", user.uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      // Create a new user document in Firestore
      const userProfile = {
        email: user.email || "",
        displayName: user.displayName || "",
        travelHistory: [],
      };
      await setDoc(userDocRef, userProfile);
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      setUser(null);
      setProfile(null);
      setTravelHistory([]);
    } catch (error) {
      // console.error("Error signing out:", error);
      // throw error;
    }
  };

  const fetchUserProfile = async (uid = user?.uid || "") => {
    if (!uid) return;
    const docRef = doc(db, "users", uid);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      const profileData = docSnap.data();
      setProfile(profileData);
      setTravelHistory(profileData.travelHistory || []);
    } else {
      console.log("No such document!");
    }
  };

  const updateProfile = async (profile) => {
    if (!user) return;
    const userDocRef = doc(db, "users", user.uid);
    await setDoc(userDocRef, profile, { merge: true });
    setProfile(profile);
  };

  // Function to update the travelHistory
  const updateTravelHistory = async (travelData) => {
    if (!user) return;

    const userDocRef = doc(db, "users", user.uid);
    const newTravelHistory = {
      from: travelData.from || "",
      to: travelData.to || "",
      month: travelData.month || "",
      mode: travelData.mode || "",
    };

    try {
      await updateDoc(userDocRef, {
        travelHistory: arrayUnion(newTravelHistory),
      });
      console.log("Travel history updated successfully");

      // Update travelHistory state locally after updating Firestore
      setTravelHistory((prev) => [...prev, newTravelHistory]);


      // Retrieve the updated document immediately to get the latest travel history
      const updatedDoc = await getDoc(userDocRef);
      if (updatedDoc.exists()) {
        const updatedData = updatedDoc.data();
        return updatedData.travelHistory; // Return the updated travel history array
      } else {
        console.error("User document does not exist.");
        return null;
      }
    } catch (error) {
      console.error("Error updating travel history: ", error);
      return null;
    }
  };

  const removeTravelHistory = async (travelData) => {
    if (!user) return;
    const userDocRef = doc(db, "users", user.uid);

    try {
      // Retrieve the current travel history
      const userDoc = await getDoc(userDocRef);
      const travelHistory = userDoc.data().travelHistory || [];

      // Find the entry to remove based on 'from' and 'to'
      const travelHistoryEntryToRemove = travelHistory.find(entry =>
        entry.from === travelData.from && entry.to === travelData.to
      );

      if (travelHistoryEntryToRemove) {
        // Remove the matching entry
        await updateDoc(userDocRef, {
          travelHistory: arrayRemove(travelHistoryEntryToRemove),
        });
        console.log("Travel history removed successfully");

        setTravelHistory((prev) =>
          prev.filter(
            (entry) =>
              !(entry.from === travelData.from && entry.to === travelData.to)
          )
        );

        // Fetch and return the updated travel history
        const updatedDoc = await getDoc(userDocRef);
        if (updatedDoc.exists()) {
          const updatedData = updatedDoc.data();
          return updatedData.travelHistory; // Return the updated travel history array
        } else {
          console.error("User document does not exist.");
          return null;
        }
      } else {
        console.log("No matching travel history entry found");
        return travelHistory; // Return original if no entry was removed
      }
    } catch (error) {
      console.error("Error removing travel history: ", error);
      return null;
    }
  };

  const reauthenticateUser = async () => {
    if (!user) return;

    // Check if the user signed in with email/password or Google
    if (user.providerData[0].providerId === "password") {
      const credential = EmailAuthProvider.credential(user.email, prompt("Please re-enter your password:"));
      await reauthenticateWithCredential(user, credential);
    } else if (user.providerData[0].providerId === "google.com") {
      const provider = new GoogleAuthProvider();
      await reauthenticateWithPopup(auth, provider);
    }
  };

  const removeAccount = async () => {
    if (!user) return;

    const userDocRef = doc(db, "users", user.uid);

    try {
      // 1. Delete the Firestore document for the user
      await deleteDoc(userDocRef);
      console.log("User data successfully deleted from Firestore");

      // 2. Delete the Firebase Authentication user
      await user.delete();
      console.log("User authentication account successfully deleted");

      // 3. Reset the user and profile states
      setUser(null);
      setProfile(null);
    } catch (error) {
      if (error.code === "auth/requires-recent-login") {
        console.log("Re-authentication required. Prompting user to re-authenticate.");
        await reauthenticateUser(); // Call re-authentication function
        await removeAccount(); // Retry account deletion after re-authentication
      } else {
        console.error("Error deleting account:", error);
        throw error;
      }
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, travelHistory, loading, signUp, signIn, signOut, signInWithGoogle, updateProfile, fetchUserProfile, updateTravelHistory, removeTravelHistory, removeAccount }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
